import React, { useState, useEffect, useRef } from "react";
import styles from "./css/UserModal.module.css";
import { Button } from "@/components/Buttons";
import { InputField } from "../inputFields/inputFields";
import SelectField from "../inputFields/SelectField";
import { CityData } from "@/types";
import { AiOutlineClose, AiOutlineCloudUpload, AiOutlineDelete, AiOutlineReload } from "react-icons/ai";
import { useSelector, useDispatch } from "react-redux";
import { useCreateCity } from "@/hooks/city/useCreateCity";
import { useUpdateCity } from "@/hooks/city/useUpdateCity";
import { setToast } from "@/store/systemSlice";
import { RootState } from "@/store/store";
import { useTranslation } from 'react-i18next';

interface AddCityModalProps {
  isOpen: boolean;
  editCity?: CityData | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddCityModal: React.FC<AddCityModalProps> = ({ isOpen, editCity, onClose, onSuccess }) => {
  const { t } = useTranslation('AddCityModal');
  const dispatch = useDispatch();
  const isEditMode = !!editCity;
  const cities = useSelector((state: RootState) => state.entity.cities) || [];
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>("");
  const [logoAction, setLogoAction] = useState<"none" | "replace" | "delete">("none");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: editCity?.name || "",
    name_en: editCity?.name_en || "",
    clone_from_city_id: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    name_en: "",
  });

  useEffect(() => {
    if (editCity) {
      setFormData({
        name: editCity.name || "",
        name_en: editCity.name_en || "",
        clone_from_city_id: "",
      });
      setLogoPreview(editCity.logo || "");
      setLogoAction("none");
    } else {
      resetForm();
    }
  }, [editCity, isOpen]);

  const createCityMutation = useCreateCity({
    onSuccess: (data) => {
      setLoading(false);
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: data.message || t("cityCreated"),
          toastType: "success",
        })
      );
      onSuccess();
      resetForm();
    },
    onError: (error: any) => {
      setLoading(false);
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: error.response?.data?.message || t("createFailed"),
          toastType: "error",
        })
      );
    },
  });

  const updateCityMutation = useUpdateCity({
    onSuccess: (data) => {
      setLoading(false);
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: data.message || t("cityUpdated"),
          toastType: "success",
        })
      );
      onSuccess();
      resetForm();
    },
    onError: (error: any) => {
      setLoading(false);
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: error.response?.data?.message || t("updateFailed"),
          toastType: "error",
        })
      );
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      name_en: "",
      clone_from_city_id: "",
    });
    setErrors({
      name: "",
      name_en: "",
    });
    setLogoFile(null);
    setLogoPreview("");
    setLogoAction("none");
  };

  const handleDataChange = (field: string, value: string | number) => {
    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        dispatch(
          setToast({
            toastMessage: "",
            toastTitle: t("logoFormatError"),
            toastType: "error",
          })
        );
        return;
      }

      setLogoFile(file);
      setLogoAction(isEditMode ? "replace" : "none");
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteLogo = () => {
    setLogoFile(null);
    setLogoPreview("");
    setLogoAction("delete");
  };

  const handleCloneChange = (city: CityData | undefined) => {
    setFormData((prev) => ({
      ...prev,
      clone_from_city_id: city ? city.id : "",
    }));
  };

  const handleSubmit = () => {
    setLoading(true);
    
    // Build plain object for PostRequestFormData
    const dataToSend: any = {
      name: formData.name,
    };
    
    if (formData.name_en) {
      dataToSend.name_en = formData.name_en;
    }
    
    if (logoFile) {
      dataToSend.logo = logoFile;
    }

    if (isEditMode && editCity) {
      dataToSend.city_id = editCity.id;
      dataToSend.upload_image_action = logoAction;
      updateCityMutation.mutate(dataToSend);
    } else {
      if (formData.clone_from_city_id) {
        dataToSend.clone_from_city_id = formData.clone_from_city_id;
      }
      createCityMutation.mutate(dataToSend);
    }
  };

  const isFormValid = () => {
    return (
      formData.name !== "" &&
      !errors.name &&
      !errors.name_en
    );
  };

  if (!isOpen) return null;

  return (
    <div className={styles.container}>
      <div className={styles.modal}>
        <AiOutlineClose className={styles.closeButton} onClick={onClose} />
        <p className={styles.username}>
          <span className={styles.usernameText}>{isEditMode ? t("editTitle") : t("createTitle")}</span>
        </p>
        <div className={styles.rows}>
          <div className={styles.row}>
            <div className={styles.inputLabel}>
              <label>{t("cityNameHebrew")} *</label>
              <InputField
                data={formData.name}
                setData={(value) => handleDataChange("name", value || '')}
                inputType="text"
                id="cityName"
                placeholder={t("cityNameHebrew")}
                errorMessage={errors.name}
                showError={!!errors.name}
              />
            </div>

            <div className={styles.inputLabel}>
              <label>{t("cityNameEnglish")}</label>
              <InputField
                data={formData.name_en || ""}
                setData={(value) => handleDataChange("name_en", value || '')}
                inputType="text"
                id="cityNameEn"
                placeholder={t("cityNameEnglish")}
                errorMessage={errors.name_en}
                showError={!!errors.name_en}
              />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.inputLabel}>
              <label>{t("logo")}</label>
              <div className={styles.logoUploadContainer}>
                {logoPreview ? (
                  <div className={styles.logoPreview}>
                    <div className={styles.logoActions}>
                      <div 
                        className={styles.iconButton}
                        onClick={() => fileInputRef.current?.click()}
                        title={t("replaceLogo")}
                      >
                        <AiOutlineReload size={18} />
                      </div>
                      <div 
                        className={styles.iconButtonDelete}
                        onClick={handleDeleteLogo}
                        title={t("deleteLogo")}
                      >
                        <AiOutlineDelete size={18} />
                      </div>
                    </div>
                    <img src={logoPreview} alt="Logo preview" />
                  </div>
                ) : (
                  <div
                    className={styles.uploadPlaceholder}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <AiOutlineCloudUpload size={48} />
                    <p>{t("uploadLogo")}</p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleLogoChange}
                  style={{ display: "none" }}
                />
              </div>
            </div>
          </div>

          {!isEditMode && (
            <div className={styles.row}>
              <div className={styles.inputLabel}>
                <label>{t("cloneFromCity")}</label>
                <SelectField
                  data={cities}
                  selectedValue={cities.find((c) => c.id === formData.clone_from_city_id) || {} as CityData}
                  updateFunction={handleCloneChange}
                  placeholder={t("selectCity")}
                  showEmpty={true}
                />
                <span className={styles.helpText}>{t("helpTextClone")}</span>
              </div>
            </div>
          )}
        </div>

        <div className={styles.buttons}>
          <Button
            text={t("cancel")}
            icon="no"
            onClick={onClose}
            className={styles.cancelButton}
            id="cancel-city-btn"
          />
          <Button
            text={loading ? (isEditMode ? t("saving") : t("creating")) : (isEditMode ? t("save") : t("create"))}
            icon="no"
            onClick={handleSubmit}
            disabled={!isFormValid() || loading}
            className={styles.submitButton}
            id="submit-city-btn"
          />
        </div>
      </div>
    </div>
  );
};

export default AddCityModal;
